import React, { useContext, useEffect, useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { User } from '../../../interfaces/common/authentication';
import ExtractAsPDFAction from './extract-as-pdf';
import { BulkActionProps } from './props.interface';
import { UserType } from '../../../enums/users';
import PrintWaybills from './print-waybills';
import CancelOrdersAction from './cancel-orders-action';
import { isMobile, isTablet } from 'react-device-detect';
import CustomDialog from '../custom-dialog/custom-dialog';
import DeliverOrdersAction from './edit-delivery-state';
import FreezeOrderDialog from './freeze-orders';
import FailDelivery from './fail-delivery';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import { DeliveryState } from '../../../enums/orders';
import ResumeDelivery from './resume-delivery';
import RestoreOrdersAction from './restore-orders';
import RerouteOrdersAction from './reroute-orders';

interface Actions {
  [key: string]: (props: any) => JSX.Element; //string index signature
}

const getCompanyActions = (user: User, orders:OrderType[], onFinish: () => void): Actions => {
  const CompanyActions: Actions = {};

  if (orders.filter(item => item.delivery_state != DeliveryState.CANCELLED).length == 0 ) {
    CompanyActions['Restore Orders'] = (props: any) => (
      <RestoreOrdersAction {...props}/>
    )
  }

  if (orders.filter(item => item.delivery_state != DeliveryState.CANCELLED).length > 0 ) {
    CompanyActions['Cancel Orders'] = (props: any) => (
      <CancelOrdersAction {...props} />
    );
  }

  CompanyActions['Freeze Orders'] = (props: any) => (
    <FreezeOrderDialog {...props} />
  );
  CompanyActions['Print Waybills'] = (props: any) => (
    <PrintWaybills {...props} onClose={onFinish} />
  );

  CompanyActions['Export PDF'] = (props: any) => (
    <ExtractAsPDFAction {...props} />
  );

  CompanyActions['Edit Delivery State'] = (props: any) => <DeliverOrdersAction {...props} />

  if (orders.some(order=>order.delivery_state==DeliveryState.PICKED_UP_FOR_DELIVERY || order.delivery_state==DeliveryState.IN_TRANSIT))
    CompanyActions['Fail Delivery Task'] = (props: any) => <FailDelivery {...props} />

  if (!orders.some(order=>order.delivery_state != DeliveryState.AWAITING_RETURN))
    CompanyActions['Resume Delivery'] = (props: any) => <ResumeDelivery {...props} />

  // Reroute Orders - available when at least one order is at warehouse
  if (orders.some(order => order.delivery_state === DeliveryState.AT_WAREHOUSE))
    CompanyActions['Reroute Orders'] = (props: any) => <RerouteOrdersAction {...props} />

  return CompanyActions;
};



const getMerchantActions = (user: User, orders:OrderType[], onFinish: () => void): Actions => {
  const CompanyActions: Actions = {};

  if (orders.filter(item => item.delivery_state != DeliveryState.CANCELLED).length == 0 ) {
    CompanyActions['Restore Orders'] = (props: any) => (
      <RestoreOrdersAction {...props}/>
    )
  }

  if (orders.filter(item => item.delivery_state != DeliveryState.CANCELLED).length > 0 ) {
    CompanyActions['Cancel Orders'] = (props: any) => (
      <CancelOrdersAction {...props} />
    );
  }



   CompanyActions['Freeze Orders'] = (props: any) => (
     <FreezeOrderDialog {...props} />
    );

    CompanyActions['Print Waybills'] = (props: any) => (
     <PrintWaybills {...props} onClose={onFinish}/>
    );

    CompanyActions['Export PDF'] = (props: any) => (
      <ExtractAsPDFAction {...props} />
    );
  
  return CompanyActions;
};

const getDefaultAction = (user: User | null): string => {
  if (!user) return '';
  if (user.type === UserType.MERCHANT) return 'Cancel Orders';
  return 'Set Delivery State';
};

const getActions = (user: User | null, orders: OrderType[], onFinish: () => void): Actions => {
  if (!user) return {};
  if (user.type === UserType.MERCHANT) {
    return getMerchantActions(user, orders, onFinish);
  }

  return getCompanyActions(user, orders, onFinish);
};
function BulkAction({
  selectedOrders,
  bulkEndpoint,
  refetchOrders,
  extractAsPdf,
}: BulkActionProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const onFinishAction = () => {
    setAction('');
    resetState();
  };

  const { user, config } = useContext(AuthContext);

  const [action, setAction] = React.useState<string>('');

  const actions = getActions(user, Array.from(selectedOrders.values()), onFinishAction);
  const orgId = user?.organisation?.org_id ?? 'UNKNOWN';

  const resetState = () => {
    setAction('');
  };

  useEffect(() => {
    resetState();
  }, []);

  return (
    <div>
      <Button
        size={(isMobile && !isTablet) ? 'small' : 'medium'}
        id="basic-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        variant="contained"
        disabled={selectedOrders.size === 0}
      >
        Actions
      </Button>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        {Object.keys(actions).map((action) => (
          <MenuItem
            onClick={() => {
              setAction(action);
              handleClose();
            }}
            key={action}
            value={action}
          >
            {action}
          </MenuItem>
        ))}
      </Menu>

      <CustomDialog fullWidth open={action.length > 0} onClose={handleClose}>
        <DialogContent>
          <br />

          <Typography
            variant="h5"
            component="h5"
            sx={{ textAlign: 'center', mt: 1, mb: 1 }}
          >
            {action}
          </Typography>

          <br />
          <br />

          {action &&
            actions[action]({
              selectedOrders,
              onFinishAction,
              bulkEndpoint,
              extractAsPdf,
              refetchOrders,
            })}
        </DialogContent>
        <DialogActions>
          <Button size={(isMobile && !isTablet) ? 'small' : 'medium'} onClick={onFinishAction}>
            Cancel
          </Button>
        </DialogActions>
      </CustomDialog>
    </div>
  );
}

export default BulkAction;
